import React, { useState } from 'react';
import Barcode from 'react-barcode';

interface ProductBarcodeProps {
    ean: string;
    width?: number;
    height?: number;
    fontSize?: number;
}

// Detect the type of EAN code
const detectEANType = (ean: string): 'EAN8' | 'EAN13' | 'CODE128' | null => {
    if (!ean) return null;
    
    // EAN-8: exactly 8 digits
    if (/^\d{8}$/.test(ean)) {
        return 'EAN8';
    }
    
    // EAN-13: exactly 13 digits
    if (/^\d{13}$/.test(ean)) {
        return 'EAN13';
    }
    
    // EAN-128 (GS1-128): alphanumeric code, generally longer
    // Can contain letters, digits, and certain special characters
    if (/^[0-9A-Za-z\-\.\s]+$/.test(ean) && ean.length >= 13) {
        return 'CODE128';
    }
    
    return null;
};

const ProductBarcode: React.FC<ProductBarcodeProps> = ({ ean, width = 1, height = 40, fontSize = 12 }) => {
    const [hasError, setHasError] = useState(false);
    const barcodeType = detectEANType(ean);
    
    if (!barcodeType) {
        return null;
    }

    // Fallback si le rendu échoue
    if (hasError) {
        return (
            <div className="flex flex-col items-center gap-1 p-3 bg-gray-50 rounded border border-gray-200">
                <span className="text-sm font-mono text-gray-800">{ean}</span>
                <span className="text-xs text-gray-500">
                    {barcodeType === 'EAN8' ? 'EAN-8' : barcodeType === 'EAN13' ? 'EAN-13' : 'EAN-128'}
                </span>
                <span className="text-xs text-red-500">Code-barres non affichable</span>
            </div>
        );
    }

    try {
        return (
            <div className="flex flex-col items-center gap-1">
                <Barcode
                    value={ean}
                    format={barcodeType}
                    width={width}
                    height={height}
                    fontSize={fontSize}
                    displayValue={true}
                    margin={0}
                />
                <span className="text-xs text-gray-500">
                    {barcodeType === 'EAN8' ? 'EAN-8' : barcodeType === 'EAN13' ? 'EAN-13' : 'EAN-128'}
                </span>
            </div>
        );
    } catch (error) {
        console.error('Erreur lors du rendu du code-barres:', error);
        return (
            <div className="flex flex-col items-center gap-1 p-3 bg-gray-50 rounded border border-gray-200">
                <span className="text-sm font-mono text-gray-800">{ean}</span>
                <span className="text-xs text-gray-500">
                    {barcodeType === 'EAN8' ? 'EAN-8' : barcodeType === 'EAN13' ? 'EAN-13' : 'EAN-128'}
                </span>
                <span className="text-xs text-red-500">Code-barres non affichable</span>
            </div>
        );
    }
};

export default ProductBarcode;
